import { supabase } from '@/lib/supabase';

type RpcArguments = Record<string, unknown>;

interface PostgrestErrorBody {
  code?: string;
  details?: string;
  hint?: string;
  message?: string;
}

/**
 * Calls a PostgREST RPC with both headers required by the Supabase gateway.
 * This is used for stock-changing operations where a missing project key must
 * fail before the browser can present the action as successful.
 */
export async function invokeAuthenticatedRpc<T = unknown>(
  functionName: string,
  args: RpcArguments,
): Promise<T | null> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('BlazePOS is missing its secure database connection settings. Reload the app or contact support.');
  }

  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  if (!session?.access_token) {
    throw new Error('Your session has expired. Sign in again before changing inventory.');
  }

  const response = await fetch(`${supabaseUrl}/rest/v1/rpc/${encodeURIComponent(functionName)}`, {
    method: 'POST',
    headers: {
      apikey: supabaseAnonKey,
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      'X-Client-Info': 'blazepos-web-transfer-rpc',
    },
    body: JSON.stringify(args),
  });

  if (!response.ok) {
    let errorBody: PostgrestErrorBody = {};
    try {
      errorBody = await response.json() as PostgrestErrorBody;
    } catch {
      // Keep the user-safe fallback below when the gateway returns non-JSON.
    }

    throw new Error(errorBody.message || `Inventory request failed with status ${response.status}`);
  }

  if (response.status === 204) return null;
  const body = await response.text();
  return body ? JSON.parse(body) as T : null;
}
